/**
 * Logic for serializing and deserializing parameters for Infographic components
 */

// Helper functions for common operations
const serializeArrayParam = (value) => {
  return Array.isArray(value) ? value.join(',') : value;
};

const deserializeArrayParam = (value) => {
  return value ? value.split(',').filter(id => id.trim()) : null;
};

const serializeMetadata = (searchParams, metadata, metadataMappings) => {
  if (!metadata) return;
  
  Object.entries(metadataMappings).forEach(([paramKey, urlKey]) => {
    if (metadata[paramKey]) {
      searchParams.set(urlKey, metadata[paramKey]);
    }
  });
};

const serializeForegroundImage = (searchParams, params) => {
  if (params.fgPictureUrl) searchParams.set('fgPictureUrl', params.fgPictureUrl);
  if (params.fgImgPositionX !== undefined) searchParams.set('fgImgPositionX', params.fgImgPositionX.toString());
  if (params.fgImgPositionY !== undefined) searchParams.set('fgImgPositionY', params.fgImgPositionY.toString());
  if (params.fgImgZoom !== undefined) searchParams.set('fgImgZoom', params.fgImgZoom.toString());
};

const serializeBackgroundImage = (searchParams, params) => {
  if (params.backgroundMode) searchParams.set('backgroundMode', params.backgroundMode);
  if (params.backgroundImgUrl) searchParams.set('backgroundImgUrl', params.backgroundImgUrl);
  if (params.backgroundColor) searchParams.set('backgroundColor', params.backgroundColor);
};

const serializeSize = (searchParams, size) => {
  if (!size) return;
  if (size.width) searchParams.set('width', size.width.toString());
  if (size.height) searchParams.set('height', size.height.toString());
};

const serializeMargin = (searchParams, margin) => {
  if (!margin) return;
  if (margin.top !== undefined) searchParams.set('marginTop', margin.top.toString());
  if (margin.left !== undefined) searchParams.set('marginLeft', margin.left.toString());
  if (margin.right !== undefined) searchParams.set('marginRight', margin.right.toString());
  if (margin.bottom !== undefined) searchParams.set('marginBottom', margin.bottom.toString());
};

const deserializeMetadata = (searchParams, metadataMappings) => {
  const metadata = {};
  let hasMetadata = false;
  
  Object.entries(metadataMappings).forEach(([paramKey, urlKey]) => {
    const value = searchParams.get(urlKey);
    if (value) {
      metadata[paramKey] = value;
      hasMetadata = true;
    }
  });
  
  return hasMetadata ? metadata : null;
};

const deserializeForegroundImage = (searchParams) => {
  const result = {};
  const fgPictureUrl = searchParams.get('fgPictureUrl');
  const fgImgPositionX = searchParams.get('fgImgPositionX');
  const fgImgPositionY = searchParams.get('fgImgPositionY');
  const fgImgZoom = searchParams.get('fgImgZoom');
  
  if (fgPictureUrl) result.fgPictureUrl = fgPictureUrl;
  if (fgImgPositionX) result.fgImgPositionX = parseInt(fgImgPositionX, 10);
  if (fgImgPositionY) result.fgImgPositionY = parseInt(fgImgPositionY, 10);
  if (fgImgZoom) result.fgImgZoom = parseInt(fgImgZoom, 10);
  
  return result;
};

const deserializeBackgroundImage = (searchParams) => {
  const result = {};
  const backgroundMode = searchParams.get('backgroundMode');
  const backgroundImgUrl = searchParams.get('backgroundImgUrl');
  const backgroundColor = searchParams.get('backgroundColor');
  
  if (backgroundMode) result.backgroundMode = backgroundMode;
  if (backgroundImgUrl) result.backgroundImgUrl = backgroundImgUrl;
  if (backgroundColor) result.backgroundColor = backgroundColor;
  
  return result;
};

const deserializeSize = (searchParams) => {
  const width = searchParams.get('width');
  const height = searchParams.get('height');
  
  if (!width && !height) return null;
  
  const size = {};
  if (width) size.width = parseInt(width, 10);
  if (height) size.height = parseInt(height, 10);
  
  return size;
};

const deserializeMargin = (searchParams) => {
  const marginTop = searchParams.get('marginTop');
  const marginLeft = searchParams.get('marginLeft');
  const marginRight = searchParams.get('marginRight');
  const marginBottom = searchParams.get('marginBottom');
  
  if (marginTop === null && marginLeft === null && marginRight === null && marginBottom === null) {
    return null;
  }
  
  const margin = {};
  if (marginTop !== null) margin.top = parseInt(marginTop, 10);
  if (marginLeft !== null) margin.left = parseInt(marginLeft, 10);
  if (marginRight !== null) margin.right = parseInt(marginRight, 10);
  if (marginBottom !== null) margin.bottom = parseInt(marginBottom, 10);
  
  return margin;
};

/**
 * Serialize IG2_DayRecap_Core parameters to URL search params
 * @param {Object} params - The parameters object
 * @returns {URLSearchParams} - URL search parameters
 */
export const serializeIG2Params = (params) => {
  const searchParams = new URLSearchParams();
  
  // Required parameters
  if (params.seasonId) searchParams.set('seasonId', params.seasonId);
  if (params.date) searchParams.set('date', params.date);
  if (params.phaseIdsAll) {
    searchParams.set('phaseIds', serializeArrayParam(params.phaseIdsAll));
  }
  if (params.venueIdsAll) {
    searchParams.set('venueIds', serializeArrayParam(params.venueIdsAll));
  }
  
  // Metadata parameters
  const ig2MetadataMappings = {
    SeasonName: 'seasonName',
    CompetitionName: 'competitionName',
    CompetitionLogo: 'competitionLogo',
    Date: 'metadataDate'
  };
  serializeMetadata(searchParams, params.metadata, ig2MetadataMappings);
  
  // Optional parameters
  serializeForegroundImage(searchParams, params);
  serializeBackgroundImage(searchParams, params);
  serializeSize(searchParams, params.size);
  serializeMargin(searchParams, params.fgImgMargin);
  
  return searchParams;
};

/**
 * Deserialize URL search params to IG2_DayRecap_Core parameters
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {Object} - The parameters object
 */
export const deserializeIG2Params = (searchParams) => {
  const params = {};
  
  // Required parameters
  const seasonId = searchParams.get('seasonId');
  if (seasonId) params.seasonId = seasonId;
  
  const date = searchParams.get('date');
  if (date) params.date = date;
  
  const phaseIds = deserializeArrayParam(searchParams.get('phaseIds'));
  if (phaseIds) params.phaseIdsAll = phaseIds;
  
  const venueIds = deserializeArrayParam(searchParams.get('venueIds'));
  if (venueIds) params.venueIdsAll = venueIds;
  
  // Metadata parameters
  const ig2MetadataMappings = {
    SeasonName: 'seasonName',
    CompetitionName: 'competitionName',
    CompetitionLogo: 'competitionLogo',
    Date: 'metadataDate'
  };
  const metadata = deserializeMetadata(searchParams, ig2MetadataMappings);
  if (metadata) params.metadata = metadata;
  
  // Optional parameters
  Object.assign(params, deserializeForegroundImage(searchParams));
  Object.assign(params, deserializeBackgroundImage(searchParams));
  
  const size = deserializeSize(searchParams);
  if (size) params.size = size;
  
  const margin = deserializeMargin(searchParams);
  if (margin) params.fgImgMargin = margin;
  
  return params;
};

/**
 * Serialize IG5_StandingsByPoints_Core parameters to URL search params
 * @param {Object} params - The parameters object
 * @returns {URLSearchParams} - URL search parameters
 */
export const serializeIG5Params = (params) => {
  const searchParams = new URLSearchParams();
  
  // Required parameters
  if (params.phaseIdsAll) {
    searchParams.set('phaseIds', serializeArrayParam(params.phaseIdsAll));
  }
  
  // Metadata parameters
  const ig5MetadataMappings = {
    GroupName: 'groupName',
    GroupColor: 'groupColor',
    SeasonName: 'seasonName',
    CompetitionName: 'competitionName',
    CompetitionLogo: 'competitionLogo',
  };
  serializeMetadata(searchParams, params.metadata, ig5MetadataMappings);
  
  // Optional parameters
  serializeForegroundImage(searchParams, params);
  serializeBackgroundImage(searchParams, params);
  serializeSize(searchParams, params.size);
  serializeMargin(searchParams, params.fgImgMargin);
  
  // Rolling display option
  if (params.isRolling !== undefined) {
    searchParams.set('isRolling', params.isRolling.toString());
  }
  if (params.rollingRowsPerPage !== undefined) {
    searchParams.set('rollingRowsPerPage', params.rollingRowsPerPage.toString());
  }
  if (params.rollingIntervalSeconds !== undefined) {
    searchParams.set('rollingIntervalSeconds', params.rollingIntervalSeconds.toString());
  }
  
  return searchParams;
};

/**
 * Deserialize URL search params to IG5_StandingsByPoints_Core parameters
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {Object} - The parameters object
 */
export const deserializeIG5Params = (searchParams) => {
  const params = {};
  
  // Required parameters
  const phaseIds = deserializeArrayParam(searchParams.get('phaseIds'));
  if (phaseIds) params.phaseIdsAll = phaseIds;
  
  // Metadata parameters
  const ig5MetadataMappings = {
    GroupName: 'groupName',
    GroupColor: 'groupColor',
    SeasonName: 'seasonName',
    CompetitionName: 'competitionName',
    CompetitionLogo: 'competitionLogo',
  };
  const metadata = deserializeMetadata(searchParams, ig5MetadataMappings);
  if (metadata) params.metadata = metadata;
  
  // Optional parameters
  Object.assign(params, deserializeForegroundImage(searchParams));
  Object.assign(params, deserializeBackgroundImage(searchParams));
  
  const size = deserializeSize(searchParams);
  if (size) params.size = size;
  
  const margin = deserializeMargin(searchParams);
  if (margin) params.fgImgMargin = margin;
  
  // Rolling display option
  const isRolling = searchParams.get('isRolling');
  if (isRolling !== null) {
    params.isRolling = isRolling === 'true';
  }
  const rollingRowsPerPage = searchParams.get('rollingRowsPerPage');
  if (rollingRowsPerPage !== null) {
    const val = parseInt(rollingRowsPerPage, 10);
    if (!isNaN(val)) params.rollingRowsPerPage = val;
  }
  const rollingIntervalSeconds = searchParams.get('rollingIntervalSeconds');
  if (rollingIntervalSeconds !== null) {
    const val = parseInt(rollingIntervalSeconds, 10);
    if (!isNaN(val)) params.rollingIntervalSeconds = val;
  }
  
  return params;
};

/**
 * Serialize IG11_PointLeaders_Core parameters to URL search params
 * @param {Object} params - The parameters object
 * @returns {URLSearchParams} - URL search parameters
 */
export const serializeIG11Params = (params) => {
  const searchParams = new URLSearchParams();
  
  // Required parameters
  if (params.phaseIdsAll) {
    searchParams.set('phaseIds', serializeArrayParam(params.phaseIdsAll));
  }
  
  // Selected teams parameter
  if (params.selectedTeams && params.selectedTeams.length > 0) {
    searchParams.set('selectedTeams', serializeArrayParam(params.selectedTeams));
  }
  
  // Top count parameter (optional; when set, limits displayed rows)
  if (params.topCount != null && params.topCount > 0) {
    searchParams.set('topCount', String(params.topCount));
  }
  
  // Metadata parameters
  const ig11MetadataMappings = {
    GroupName: 'groupName',
    GroupColor: 'groupColor',
    SeasonName: 'seasonName',
    CompetitionName: 'competitionName',
    CompetitionLogo: 'competitionLogo',
  };
  serializeMetadata(searchParams, params.metadata, ig11MetadataMappings);
  
  // Optional parameters
  serializeForegroundImage(searchParams, params);
  serializeBackgroundImage(searchParams, params);
  serializeSize(searchParams, params.size);
  serializeMargin(searchParams, params.fgImgMargin);
  
  return searchParams;
};

/**
 * Deserialize URL search params to IG11_PointLeaders_Core parameters
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {Object} - The parameters object
 */
export const deserializeIG11Params = (searchParams) => {
  const params = {};
  
  // Required parameters
  const phaseIds = deserializeArrayParam(searchParams.get('phaseIds'));
  if (phaseIds) params.phaseIdsAll = phaseIds;
  
  // Selected teams parameter
  const selectedTeams = deserializeArrayParam(searchParams.get('selectedTeams'));
  if (selectedTeams) params.selectedTeams = selectedTeams;
  
  // Top count parameter
  const topCountParam = searchParams.get('topCount');
  if (topCountParam != null) {
    const n = parseInt(topCountParam, 10);
    if (!Number.isNaN(n)) params.topCount = n;
  }
  
  // Metadata parameters
  const ig11MetadataMappings = {
    GroupName: 'groupName',
    GroupColor: 'groupColor',
    SeasonName: 'seasonName',
    CompetitionName: 'competitionName',
    CompetitionLogo: 'competitionLogo',
  };
  const metadata = deserializeMetadata(searchParams, ig11MetadataMappings);
  if (metadata) params.metadata = metadata;
  
  // Optional parameters
  Object.assign(params, deserializeForegroundImage(searchParams));
  Object.assign(params, deserializeBackgroundImage(searchParams));
  
  const size = deserializeSize(searchParams);
  if (size) params.size = size;
  
  const margin = deserializeMargin(searchParams);
  if (margin) params.fgImgMargin = margin;
  
  return params;
};

/**
 * Serialize IG12_GoalLeaders_Core parameters to URL search params
 * @param {Object} params - The parameters object
 * @returns {URLSearchParams} - URL search parameters
 */
export const serializeIG12Params = (params) => {
  const searchParams = new URLSearchParams();
  
  // Required parameters
  if (params.phaseIdsAll) {
    searchParams.set('phaseIds', serializeArrayParam(params.phaseIdsAll));
  }
  
  // Selected teams parameter
  if (params.selectedTeams && params.selectedTeams.length > 0) {
    searchParams.set('selectedTeams', serializeArrayParam(params.selectedTeams));
  }
  
  // Metadata parameters
  const ig12MetadataMappings = {
    GroupName: 'groupName',
    GroupColor: 'groupColor',
    SeasonName: 'seasonName',
    CompetitionName: 'competitionName',
    CompetitionLogo: 'competitionLogo'
  };
  serializeMetadata(searchParams, params.metadata, ig12MetadataMappings);
  
  // Optional parameters
  serializeForegroundImage(searchParams, params);
  serializeBackgroundImage(searchParams, params);
  serializeSize(searchParams, params.size);
  serializeMargin(searchParams, params.fgImgMargin);
  
  return searchParams;
};

/**
 * Deserialize URL search params to IG12_GoalLeaders_Core parameters
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {Object} - The parameters object
 */
export const deserializeIG12Params = (searchParams) => {
  const params = {};
  
  // Required parameters
  const phaseIds = deserializeArrayParam(searchParams.get('phaseIds'));
  if (phaseIds) params.phaseIdsAll = phaseIds;
  
  // Selected teams parameter
  const selectedTeams = deserializeArrayParam(searchParams.get('selectedTeams'));
  if (selectedTeams) params.selectedTeams = selectedTeams;
  
  // Metadata parameters
  const ig12MetadataMappings = {
    GroupName: 'groupName',
    GroupColor: 'groupColor',
    SeasonName: 'seasonName',
    CompetitionName: 'competitionName',
    CompetitionLogo: 'competitionLogo'
  };
  const metadata = deserializeMetadata(searchParams, ig12MetadataMappings);
  if (metadata) params.metadata = metadata;
  
  // Optional parameters
  Object.assign(params, deserializeForegroundImage(searchParams));
  Object.assign(params, deserializeBackgroundImage(searchParams));
  
  const size = deserializeSize(searchParams);
  if (size) params.size = size;
  
  const margin = deserializeMargin(searchParams);
  if (margin) params.fgImgMargin = margin;
  
  return params;
};

/**
 * Serialize IG15_PenaltyLeaders_Core parameters to URL search params
 * @param {Object} params - The parameters object
 * @returns {URLSearchParams} - URL search parameters
 */
export const serializeIG15Params = (params) => {
  const searchParams = new URLSearchParams();
  
  // Required parameters
  if (params.phaseIdsAll) {
    searchParams.set('phaseIds', serializeArrayParam(params.phaseIdsAll));
  }
  
  // Selected teams parameter
  if (params.selectedTeams && params.selectedTeams.length > 0) {
    searchParams.set('selectedTeams', serializeArrayParam(params.selectedTeams));
  }
  
  // Metadata parameters
  const ig15MetadataMappings = {
    GroupName: 'groupName',
    GroupColor: 'groupColor',
    SeasonName: 'seasonName',
    CompetitionName: 'competitionName',
    CompetitionLogo: 'competitionLogo',
  };
  serializeMetadata(searchParams, params.metadata, ig15MetadataMappings);
  
  // Optional parameters
  serializeForegroundImage(searchParams, params);
  serializeBackgroundImage(searchParams, params);
  serializeSize(searchParams, params.size);
  serializeMargin(searchParams, params.fgImgMargin);
  
  return searchParams;
};

/**
 * Deserialize URL search params to IG15_PenaltyLeaders_Core parameters
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {Object} - The parameters object
 */
export const deserializeIG15Params = (searchParams) => {
  const params = {};
  
  // Required parameters
  const phaseIds = deserializeArrayParam(searchParams.get('phaseIds'));
  if (phaseIds) params.phaseIdsAll = phaseIds;
  
  // Selected teams parameter
  const selectedTeams = deserializeArrayParam(searchParams.get('selectedTeams'));
  if (selectedTeams) params.selectedTeams = selectedTeams;
  
  // Metadata parameters
  const ig15MetadataMappings = {
    GroupName: 'groupName',
    GroupColor: 'groupColor',
    SeasonName: 'seasonName',
    CompetitionName: 'competitionName',
    CompetitionLogo: 'competitionLogo',
  };
  const metadata = deserializeMetadata(searchParams, ig15MetadataMappings);
  if (metadata) params.metadata = metadata;
  
  // Optional parameters
  Object.assign(params, deserializeForegroundImage(searchParams));
  Object.assign(params, deserializeBackgroundImage(searchParams));
  
  const size = deserializeSize(searchParams);
  if (size) params.size = size;
  
  const margin = deserializeMargin(searchParams);
  if (margin) params.fgImgMargin = margin;
  
  return params;
};

/**
 * Generate embed URL for a specific infographic
 * @param {string} igId - Infographic identifier (e.g., 'IG2')
 * @param {Object} params - Parameters for the infographic
 * @returns {string} - Complete embed URL
 */
export const generateEmbedUrl = (igId, params) => {
  const baseUrl = window.location.origin;
  const embedPath = '/embed/ig';
  
  let searchParams;
  switch (igId) {
    case 'IG2':
      searchParams = serializeIG2Params(params);
      break;
    case 'IG5':
      searchParams = serializeIG5Params(params);
      break;
    case 'IG11':
      searchParams = serializeIG11Params(params);
      break;
    case 'IG12':
      searchParams = serializeIG12Params(params);
      break;
    case 'IG15':
      searchParams = serializeIG15Params(params);
      break;
    default:
      throw new Error(`Unsupported infographic ID: ${igId}`);
  }
  
  // Add the IG identifier
  searchParams.set('ig', igId);
  
  return `${baseUrl}${embedPath}?${searchParams.toString()}`;
};

/**
 * Parse embed URL and extract infographic ID and parameters
 * @param {string} url - The embed URL
 * @returns {Object} - Object containing igId and params
 */
export const parseEmbedUrl = (url) => {
  const urlObj = new URL(url);
  const searchParams = new URLSearchParams(urlObj.search);
  
  const igId = searchParams.get('ig');
  if (!igId) {
    throw new Error('No infographic ID found in URL');
  }
  
  let params;
  switch (igId) {
    case 'IG2':
      params = deserializeIG2Params(searchParams);
      break;
    case 'IG5':
      params = deserializeIG5Params(searchParams);
      break;
    case 'IG11':
      params = deserializeIG11Params(searchParams);
      break;
    case 'IG12':
      params = deserializeIG12Params(searchParams);
      break;
    case 'IG15':
      params = deserializeIG15Params(searchParams);
      break;
    default:
      throw new Error(`Unsupported infographic ID: ${igId}`);
  }
  
  return { igId, params };
};
